import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Search, MapPin, Wifi, Check, Clock, AlertTriangle, ChevronRight, ChevronUp, ChevronDown, Filter } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { searchAddress, checkCoverage, formatAddress } from './api';

// API base URL for Cloudflare Functions - same as in api.js
const API_BASE_URL = '/api';
import AddressSearchResults from './AddressSearchResults';
import CoverageResults from './CoverageResults';

const CoverageSidebar = ({
  isOpen,
  onClose,
  initialCoordinates = null,
  initialAddress = null,
  openPackageComparisonModal,
  onOpenRecommendations = () => {},
  isSideBySideLayout = false,
  updateFilters = () => {},
  selectedOperators = [],
  setSelectedOperators = () => {},
  speedFilter = [0, 1000],
  setSpeedFilter = () => {},
  priceFilter = [0, 2000],
  setPriceFilter = () => {},
  sortBy = 'price',
  setSortBy = () => {},
  sortDirection = 'asc',
  setSortDirection = () => {},
  isUsingGeolocation = false,
  setIsUsingGeolocation = () => {},
  // Lifted state from parent
  selectedAddress = null,
  setSelectedAddress = () => {},
  // selectedPlaceCoordinates = null, // Removed state
  setSelectedPlaceCoordinates = () => {} // Keep setter prop for now, might be needed? Revisit.
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  // selectedAddress and selectedPlaceCoordinates are now props
  const [coverageResult, setCoverageResult] = useState(null);
  const [isCheckingCoverage, setIsCheckingCoverage] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [error, setError] = useState(null);
  const [isAddressSelected, setIsAddressSelected] = useState(false);
  const [progressStage, setProgressStage] = useState(null); // 'searching', 'getting-details', 'checking-coverage'
  const [progressMessage, setProgressMessage] = useState('');
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 768);
  // Always expanded on mobile
  const [sheetExpanded, setSheetExpanded] = useState(true);
  const [filterExpanded, setFilterExpanded] = useState(false);
  
  // Check if we're on mobile
  const isMobile = windowWidth < 768;
  
  // Handle window resize
  const handleResize = useCallback(() => {
    setWindowWidth(window.innerWidth);
  }, []);
  
  // Set up resize listener
  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [handleResize]);
  
  const searchInputRef = useRef(null);
  const searchContainerRef = useRef(null);
  const sheetRef = useRef(null);
  
  // Handle initial coordinates and address if provided
  useEffect(() => {
    if (isOpen && initialCoordinates && initialAddress) {
      setSelectedPlaceCoordinates(initialCoordinates);
      setSelectedAddress(initialAddress);
      setSearchQuery(initialAddress.description || initialAddress.mainText);
      
      // Check if this is a geolocation address
      if (initialAddress.mainText === "Current Location") {
        setIsUsingGeolocation(true);
      } else {
        setIsUsingGeolocation(false);
      }
      
      handleCoverageCheck(initialCoordinates);
    }
  }, [isOpen, initialCoordinates, initialAddress]);
  
  // Focus the search input when the sidebar opens
  useEffect(() => {
    if (isOpen && searchInputRef.current && !initialCoordinates) {
      setTimeout(() => {
        searchInputRef.current.focus();
      }, 300); // Delay to allow animation to complete
    }
  }, [isOpen, initialCoordinates]);
  
  // Modified to prevent body scrolling without affecting navigation visibility
  useEffect(() => {
    if (isOpen) {
      // Save the current scroll position
      const scrollY = window.scrollY;
      
      // Instead of setting position: fixed on the body, just prevent scrolling
      document.body.style.overflow = 'hidden';
      
      return () => {
        // Restore scrolling when component unmounts or sidebar closes
        document.body.style.overflow = '';
        
        // Restore scroll position
        window.scrollTo(0, scrollY);
      };
    }
  }, [isOpen]);
  
  // Reset state when sidebar closes
  useEffect(() => {
    if (!isOpen) {
      // Wait for the closing animation to complete before resetting state
      const timer = setTimeout(() => {
        setSearchQuery('');
        setSearchResults([]);
        // Don't reset selectedAddress and selectedPlaceCoordinates here
        // as they're now managed by the parent component
        setCoverageResult(null);
        setShowResults(false);
        setError(null);
        setProgressStage(null);
        setProgressMessage('');
        setIsUsingGeolocation(false);
      }, 500);
      
      return () => clearTimeout(timer);
    }
  }, [isOpen]);
  
  // Handle click outside search results
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
        setSearchResults([]);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  // Handle address search
  useEffect(() => {
    // Skip search if an address was just selected
    if (isAddressSelected) {
      setIsAddressSelected(false);
      return;
    }
    
    const delayDebounceFn = setTimeout(async () => {
      if (searchQuery.length >= 4 && !isUsingGeolocation) { // Minimum 4 characters as per API requirement
        setIsSearching(true);
        setError(null);
        setProgressStage('searching');
        setProgressMessage('Searching for addresses...');
        
        try {
          const response = await searchAddress(searchQuery);
          setSearchResults(response.results);
        } catch (error) {
          console.error('Error searching for address:', error);
          setError('Failed to search for address. Please try again.');
        } finally {
          setIsSearching(false);
          setProgressStage(null);
          setProgressMessage('');
        }
      } else {
        setSearchResults([]);
      }
    }, 300);
    
    return () => clearTimeout(delayDebounceFn);
  }, [searchQuery, isAddressSelected, isUsingGeolocation]);
  
  const handleAddressSelect = async (address) => {
    setSelectedAddress(address);
    setIsAddressSelected(true); // Set flag to prevent search from triggering
    setSearchQuery(address.description || address.mainText);
    setSearchResults([]); // Clear search results immediately
    setError(null);
    setIsUsingGeolocation(false);
    
    // Get place details to get coordinates
    try {
      setProgressStage('getting-details');
      setProgressMessage('Getting address details...');
      console.log('Getting place details for:', address.placeId);
      
      // Use the same API endpoint as the NavigationBar component
      const response = await fetch(`${API_BASE_URL}/place-details?place_id=${encodeURIComponent(address.placeId)}`);
      
      if (!response.ok) {
        throw new Error(`Error: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('Place details response:', data);
      
      if (data && data.result && data.result.geometry && data.result.geometry.location) {
        const placeDetails = data.result;
        console.log('Got coordinates:', placeDetails.geometry.location);
        
        // Extract province name
        let provinceName = '';
        if (placeDetails.address_components) {
          const provinceComponent = placeDetails.address_components.find(comp =>
            comp.types.includes('administrative_area_level_1')
          );
          if (provinceComponent) {
            provinceName = provinceComponent.long_name;
            console.log('Extracted Province:', provinceName);
          } else {
            console.warn('Province (administrative_area_level_1) not found in address components.');
          }
        }

        // Update selected address object to include province
        const updatedAddress = {
          ...address,
          provinceName: provinceName,
          // Also store the full placeDetails for potential future use
          placeDetails: placeDetails
        };
        setSelectedAddress(updatedAddress); // Update the parent state with the enriched address object

        // Automatically check coverage when coordinates are available
        handleCoverageCheck(placeDetails.geometry.location);
      } else {
        throw new Error('Invalid place details response');
      }
    } catch (error) {
      console.error('Error getting place details:', error);
      setError('Failed to get place details. Please try again.');
      setProgressStage(null);
      setProgressMessage('');
    }
  };
  
  // Handle coverage check with coordinates
  const handleCoverageCheck = async (coordinates) => {
    if (!coordinates) {
      setError('No coordinates available. Please try selecting an address again.');
      return;
    }
    
    console.log('Checking coverage for coordinates:', coordinates);
    setShowResults(true);
    setIsCheckingCoverage(true);
    setError(null);
    setProgressStage('checking-coverage');
    setProgressMessage('Checking coverage at your location...');
    
    try {
      const result = await checkCoverage(coordinates);
      setCoverageResult(result);
      
      // Automatically open the package comparison modal if coverage is available
      if (result.status === 'available' && result.coverageResults && result.coverageResults.length > 0) {
        // Select all operators by default, but store both operator name and connection type
        const allOperators = result.coverageResults.map(op => ({
          name: op.operatorName,
          displayName: op.displayName || op.operatorName,
          connectionType: op.connectionType || 'FTTH'
        }));
        
        // Store just the unique identifiers in selectedOperators
        const allOperatorIdentifiers = allOperators.map(op =>
          `${op.name}:${op.connectionType}`
        );
        setSelectedOperators(allOperatorIdentifiers);
        
        // Open the comparison modal with all available operators and close the sidebar only on mobile
        // Use setTimeout to ensure the sidebar is fully rendered before opening the modal
        setTimeout(() => {
          openPackageComparisonModal(result.coverageResults[0], result.coverageResults);
          // Close the sidebar after opening the comparison modal only on mobile
          if (isMobile) {
            onClose();
          }
        }, 300);
      }
    } catch (error) {
      console.error('Error checking coverage:', error);
      setCoverageResult({
        status: 'error',
        message: 'Sorry, we encountered an error checking coverage.',
        details: 'Please try again later or contact our support team.'
      });
      setError('Failed to check coverage. Please try again.');
    } finally {
      setIsCheckingCoverage(false);
      setProgressStage(null);
      setProgressMessage('');
    }
  };
  
  const handleClearSearch = () => {
    setSearchQuery('');
    setSelectedAddress(null); // Still clear the address in parent
    setSelectedPlaceCoordinates(null); // Still clear the coordinates in parent
    setCoverageResult(null);
    setShowResults(false);
    setIsAddressSelected(false);
    setSearchResults([]);
    setIsUsingGeolocation(false);
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };
  
  // Animation variants
  const backdropVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { duration: 0.3, ease: 'easeOut' }
    },
    exit: { 
      opacity: 0,
      transition: { duration: 0.3, ease: 'easeIn', delay: 0.1 }
    }
  };
  
  const sidebarVariants = {
    hidden: isMobile
      ? {
          y: '-100%',
          transition: { duration: 0.4, ease: [0.16, 1, 0.3, 1] }
        }
      : {
          x: '-100%',
          transition: { duration: 0.4, ease: [0.16, 1, 0.3, 1] }
        },
    visible: isMobile
      ? {
          y: 0, // Always fully expanded on mobile
          transition: {
            duration: 0.4,
            ease: [0.16, 1, 0.3, 1],
            when: 'beforeChildren',
            staggerChildren: 0.1
          }
        }
      : {
          x: 0,
          transition: {
            duration: 0.4,
            ease: [0.16, 1, 0.3, 1],
            when: 'beforeChildren',
            staggerChildren: 0.1
          }
        },
    exit: isMobile
      ? {
          y: '-100%',
          transition: {
            duration: 0.4,
            ease: [0.16, 1, 0.3, 1],
            when: 'afterChildren',
            staggerChildren: 0.05,
            staggerDirection: -1
          }
        }
      : {
          x: '-100%',
          transition: {
            duration: 0.4,
            ease: [0.16, 1, 0.3, 1],
            when: 'afterChildren',
            staggerChildren: 0.05,
            staggerDirection: -1
          }
        }
  };
  
  const contentVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.3, ease: 'easeOut' }
    },
    exit: { 
      opacity: 0, 
      y: 5,
      transition: { duration: 0.2, ease: 'easeIn' }
    }
  };
  
  // Progress indicator component
  const ProgressIndicator = ({ stage, message }) => {
    if (!stage) return null;
    
    return (
      <motion.div 
        className="absolute inset-x-0 top-0 bg-gradient-to-r from-purple-600 to-pink-600 text-white py-2 px-4 text-sm flex items-center justify-center"
        initial={{ y: -40 }}
        animate={{ y: 0 }}
        exit={{ y: -40 }}
      >
        <svg className="animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        {message}
      </motion.div>
    );
  };
  
  // Handle sidebar close with proper cleanup
  const handleClose = () => {
    onClose();
  };
  
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className={`fixed ${isMobile ? 'inset-x-0 top-16 bottom-0 z-70' : 'top-16 bottom-0 left-0 z-60 flex'} ${isSideBySideLayout && !isMobile ? 'w-1/4' : isMobile ? 'w-full' : 'w-full md:w-[400px]'}`}
          initial="hidden"
          animate="visible"
          exit="exit"
        >
          {/* Backdrop - only covers the content area, not the navigation */}
          {!isSideBySideLayout && !isMobile && (
            <motion.div
              className="absolute inset-0 bg-gray-900/70 backdrop-blur-sm"
              variants={backdropVariants}
              onClick={handleClose}
            />
          )}
          
          {/* Mobile backdrop - covers the entire screen when sheet is expanded */}
          {isMobile && sheetExpanded && (
            <motion.div
              className="fixed inset-0 bg-gray-900/70 backdrop-blur-sm"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSheetExpanded(false)}
            />
          )}
          
          {/* Sidebar/Bottom Sheet */}
          <motion.div
            ref={sheetRef}
            className={`relative ${isMobile ? 'w-full rounded-t-xl' : 'w-full'} bg-gradient-to-b from-gray-900 to-gray-800 shadow-2xl ${isMobile ? '' : 'border-r'} border-gray-800 flex flex-col h-full`}
            variants={sidebarVariants}
            drag={isMobile ? "y" : false}
            dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={0.1}
            onDragEnd={(e, info) => {
              if (isMobile) {
                if (info.offset.y > 100) {
                  setSheetExpanded(false);
                } else if (info.offset.y < -100) {
                  setSheetExpanded(true);
                }
              }
            }}
          >
            {/* No mobile sheet handle needed since we're always showing the full sidebar */}
            {/* Progress Indicator */}
            <AnimatePresence>
              {progressStage && (
                <ProgressIndicator stage={progressStage} message={progressMessage} />
              )}
            </AnimatePresence>
            
            {/* Close button */}
            <motion.button
              className="absolute top-4 right-4 text-gray-400 hover:text-white z-10 p-1 hover:bg-gray-800/50 rounded-full transition-colors"
              onClick={handleClose}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <X className="h-5 w-5" />
            </motion.button>
            
            {/* Header */}
            {!isMobile || (isMobile && sheetExpanded) ? (
              <motion.div
                className="p-5 border-b border-gray-800 flex-shrink-0"
                variants={contentVariants}
              >
                <div className="flex items-center mb-1">
                  <Wifi className="h-5 w-5 text-purple-400 mr-2" />
                  <h2 className="text-xl font-bold text-white">Check Coverage</h2>
                </div>
                <p className="text-gray-400 text-sm">Enter your address to check if high-speed internet is available at your location.</p>
              </motion.div>
            ) : null}
            
            {/* Scrollable Content Container */}
            {!isMobile || (isMobile && sheetExpanded) ? (
              <motion.div
                className="flex-1 overflow-y-auto"
                variants={contentVariants}
              >
              {/* Search container */}
              <div className="p-5">
                <div
                  ref={searchContainerRef}
                  className="relative mb-5"
                >
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      {isSearching ? (
                        <svg className="animate-spin h-4 w-4 text-purple-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                      ) : (
                        <MapPin className="h-4 w-4 text-purple-400" />
                      )}
                    </div>
                    <input
                      ref={searchInputRef}
                      type="text"
                      placeholder="Enter your address..."
                      value={searchQuery}
                      onChange={(e) => {
                        setIsAddressSelected(false);
                        setSearchQuery(e.target.value);
                        if (isUsingGeolocation) {
                          setIsUsingGeolocation(false);
                        }
                      }}
                      className={`w-full py-2.5 pl-10 pr-10 rounded-lg bg-gray-800 text-white placeholder-gray-400 focus:ring-2 focus:ring-purple-500 border-none text-sm ${isCheckingCoverage ? 'opacity-75' : ''}`}
                      disabled={isCheckingCoverage}
                    />
                    {searchQuery && (
                      <motion.button
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                        onClick={handleClearSearch}
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                      >
                        <X className="h-4 w-4 text-gray-400 hover:text-white transition-colors" />
                      </motion.button>
                    )}
                  </div>
                  
                  {/* Search Results Dropdown */}
                  <AddressSearchResults
                    results={searchResults}
                    isLoading={isSearching}
                    onSelect={handleAddressSelect}
                    isDarkTheme={true}
                  />
                </div>
                
                {/* Error Message */}
                <AnimatePresence>
                  {error && (
                    <motion.div 
                      className="mb-4 p-3 bg-red-900/50 text-white rounded-lg text-sm border border-red-800/50"
                      initial={{ opacity: 0, y: -5 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -5 }}
                    >
                      {error}
                    </motion.div>
                  )}
                </AnimatePresence>
                
                {/* Manual search button */}
                <motion.button
                  className={`w-full py-2.5 px-4 rounded-lg ${
                    isCheckingCoverage
                      ? 'bg-purple-600 cursor-wait'
                      : 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700'
                  } text-white font-medium flex items-center justify-center`}
                  onClick={() => {
                    if (selectedAddress && selectedPlaceCoordinates) {
                      handleCoverageCheck(selectedPlaceCoordinates);
                    } else {
                      setError('Please select an address from the suggestions first.');
                    }
                  }}
                  disabled={isCheckingCoverage || !selectedAddress}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {isCheckingCoverage ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Checking Coverage...
                    </>
                  ) : (
                    <>
                      <Search className="h-4 w-4 mr-2" />
                      Check Coverage
                    </>
                  )}
                </motion.button>
              </div>
              
              {/* Results */}
              <AnimatePresence>
                {showResults && coverageResult && (
                  <motion.div
                    className="p-5 border-t border-gray-800"
                    variants={contentVariants}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                  >
                    <div className="mb-4">
                      <div className="text-gray-400 text-xs mb-1 flex items-center">
                        {selectedAddress && (
                          <>
                            <span className="truncate">
                              {formatAddress(selectedAddress.description || `${selectedAddress.mainText}, ${selectedAddress.secondaryText}`)}
                            </span>
                            {isUsingGeolocation && (
                              <span className="ml-1 text-amber-400 flex-shrink-0 flex items-center">
                                <AlertTriangle className="h-3 w-3 mr-0.5" />
                                (approximate)
                              </span>
                            )}
                          </>
                        )}
                      </div>
                      <motion.h3
                        className={`text-lg font-bold ${
                          coverageResult.status === 'available' ? 'text-green-400' :
                          coverageResult.status === 'coming_soon' ? 'text-amber-400' :
                          coverageResult.status === 'error' ? 'text-red-400' : 'text-gray-300'
                        }`}
                        initial={{ opacity: 0, y: 5 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                      >
                        {coverageResult.message}
                      </motion.h3>
                      <motion.p
                        className="text-gray-300 text-sm mt-1"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.2 }}
                      >
                        {coverageResult.details}
                      </motion.p>
                      
                      {/* No geolocation warning here - it's now in the parent component */}
                    </div>
                    
                    {/* Available Network Operators */}
                    {coverageResult.status === 'available' && coverageResult.coverageResults && (
                      <div className="space-y-4">
                        <div className="flex justify-between items-center mb-2">
                          <h3 className="text-sm font-medium text-white flex items-center">
                            <Wifi className="h-4 w-4 text-purple-400 mr-2" />
                            Available Networks
                          </h3>
                          <span className="text-xs text-gray-400 bg-gray-800/50 px-2 py-0.5 rounded-full">
                            {coverageResult.coverageResults.length} provider{coverageResult.coverageResults.length !== 1 ? 's' : ''}
                          </span>
                        </div>
                        
                        {/* Network Operator Selection */}
                        <div className="space-y-2">
                          <div className="text-xs font-medium text-gray-300 mb-2">Select Providers to Compare</div>
                          <div className="flex flex-wrap gap-2">
                            {coverageResult.coverageResults.map((operator, index) => {
                              const operatorIdentifier = `${operator.operatorName}:${operator.connectionType || 'FTTH'}`;
                              const isSelected = selectedOperators.includes(operatorIdentifier);
                              const displayName = operator.displayName || operator.operatorName;
                              
                              // Skip Frogfoot Business as we don't have packages for it
                              if (operator.operatorName.toLowerCase().includes('frogfoot') &&
                                  (operator.connectionType === 'FTTB' || operator.networkType?.toLowerCase().includes('business'))) {
                                return null;
                              }
                              
                              return (
                                <button
                                  key={index}
                                  className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center space-x-1 transition-colors ${
                                    isSelected
                                      ? 'bg-purple-600 text-white'
                                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                                  }`}
                                  onClick={() => {
                                    if (isSelected) {
                                      // Don't allow deselecting the last operator
                                      if (selectedOperators.length > 1) {
                                        setSelectedOperators(selectedOperators.filter(id => id !== operatorIdentifier));
                                      }
                                    } else {
                                      setSelectedOperators([...selectedOperators, operatorIdentifier]);
                                    }
                                  }}
                                >
                                  <span>{displayName}</span>
                                  {isSelected && <Check className="h-3 w-3 ml-1" />}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                        
                        {/* Filters */}
                        <div className={`space-y-3 mt-4 ${isMobile && !filterExpanded ? 'hidden' : ''}`}>
                          <div className="flex justify-between items-center">
                            <h3 className="text-sm font-medium text-white">Filters</h3>
                          </div>
                          
                          {/* Sort Controls */}
                          <div className="space-y-2">
                            <div className="text-xs font-medium text-gray-300">Sort by:</div>
                            <div className="flex rounded-lg overflow-hidden">
                              <button
                                className={`px-3 py-1.5 text-xs font-medium flex items-center ${
                                  sortBy === 'price'
                                    ? 'bg-purple-600 text-white'
                                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                                }`}
                                onClick={() => {
                                  if (sortBy === 'price') {
                                    setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
                                  } else {
                                    setSortBy('price');
                                    setSortDirection('asc');
                                  }
                                }}
                              >
                                Price
                                {sortBy === 'price' && (
                                  sortDirection === 'asc'
                                    ? <ChevronUp className="h-3 w-3 ml-1" />
                                    : <ChevronDown className="h-3 w-3 ml-1" />
                                )}
                              </button>
                              <button
                                className={`px-3 py-1.5 text-xs font-medium flex items-center ${
                                  sortBy === 'speed'
                                    ? 'bg-purple-600 text-white'
                                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                                }`}
                                onClick={() => {
                                  if (sortBy === 'speed') {
                                    setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
                                  } else {
                                    setSortBy('speed');
                                    setSortDirection('asc');
                                  }
                                }}
                              >
                                Speed
                                {sortBy === 'speed' && (
                                  sortDirection === 'asc'
                                    ? <ChevronUp className="h-3 w-3 ml-1" />
                                    : <ChevronDown className="h-3 w-3 ml-1" />
                                )}
                              </button>
                              {/* Connection Type Filter Button */}
                              <button
                                className={`px-3 py-1.5 text-xs font-medium flex items-center ${
                                  sortBy === 'type'
                                    ? 'bg-purple-600 text-white'
                                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                                }`}
                                onClick={() => {
                                  if (sortBy === 'type') {
                                    setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
                                  } else {
                                    setSortBy('type');
                                    setSortDirection('asc');
                                  }
                                }}
                              >
                                Connection Type
                                {sortBy === 'type' && (
                                  sortDirection === 'asc'
                                    ? <ChevronUp className="h-3 w-3 ml-1" />
                                    : <ChevronDown className="h-3 w-3 ml-1" />
                                )}
                              </button>
                            </div>
                          </div>
                          
                          {/* Speed Range Filter */}
                          <div className="space-y-2">
                            <div className="flex justify-between mb-1">
                              <label className="text-xs font-medium text-gray-300">Speed Range</label>
                              <span className="text-xs text-gray-400">
                                {speedFilter[0]} - {speedFilter[1] === 1000 ? '1000+' : speedFilter[1]} Mbps
                              </span>
                            </div>
                            <input
                              type="range"
                              min="0"
                              max="1000"
                              step="10"
                              value={speedFilter[1]}
                              onChange={(e) => setSpeedFilter([speedFilter[0], parseInt(e.target.value)])}
                              className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-purple-500"
                            />
                          </div>
                          
                          {/* Price Range Filter */}
                          <div className="space-y-2">
                            <div className="flex justify-between mb-1">
                              <label className="text-xs font-medium text-gray-300">Price Range</label>
                              <span className="text-xs text-gray-400">
                                R{priceFilter[0]} - R{priceFilter[1] === 2000 ? '2000+' : priceFilter[1]}
                              </span>
                            </div>
                            <input
                              type="range"
                              min="0"
                              max="2000"
                              step="50"
                              value={priceFilter[1]}
                              onChange={(e) => setPriceFilter([priceFilter[0], parseInt(e.target.value)])}
                              className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-purple-500"
                            />
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {coverageResult.status === 'coming_soon' && (
                      <motion.button
                        className="mt-4 w-full py-2.5 px-4 bg-gradient-to-r from-amber-600 to-amber-500 hover:from-amber-700 hover:to-amber-600 text-white rounded-lg font-medium"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3 }}
                      >
                        Notify me when available
                      </motion.button>
                    )}
                    
                    {coverageResult.status === 'not_available' && coverageResult.alternatives && (
                      <motion.div
                        className="mt-4 space-y-2"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.3 }}
                      >
                        <h4 className="text-sm font-medium text-gray-300 mb-2">Alternative Options:</h4>
                        {coverageResult.alternatives.map((alt, index) => (
                          <motion.button
                            key={index}
                            className="w-full py-2.5 px-4 bg-gray-800 text-gray-200 rounded-lg hover:bg-gray-700 transition-colors flex items-center justify-between"
                            whileHover={{ scale: 1.02, x: 3 }}
                            whileTap={{ scale: 0.98 }}
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.3 + (index * 0.1) }}
                          >
                            <span>{alt} options</span>
                            <ChevronRight className="h-4 w-4" />
                          </motion.button>
                        ))}
                      </motion.div>
                    )}
                    
                    {coverageResult.status === 'available' && (
                      <>
                        {/* No filter button needed here since it's in the comparison modal */}
                        
                        {/* Get a Recommendation Button */}
                        <motion.button
                          className="mt-5 w-full py-2.5 px-4 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white rounded-lg font-medium flex items-center justify-center"
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.3 }}
                          onClick={() => {
                            // Ensure onOpenRecommendations is called properly
                            if (onOpenRecommendations && typeof onOpenRecommendations === 'function') {
                              // First close the package comparison modal if it's open
                              // This is important because the PackageComparisonModal is linked to the CoverageSidebar
                              if (typeof openPackageComparisonModal === 'function') {
                                // Call with null parameters to close it
                                openPackageComparisonModal(null, []);
                              }
                              
                              // Then close the coverage sidebar
                              onClose();
                              
                              // Finally open the recommendations modal after a small delay
                              // This ensures both the sidebar and package comparison modal are closed first
                              setTimeout(() => {
                                onOpenRecommendations();
                              }, 200);
                            }
                          }}
                        >
                          <span>Get a Recommendation</span>
                          <ChevronRight className="h-4 w-4 ml-2" />
                        </motion.button>
                        
                        {/* Add padding at the bottom for mobile to avoid bottom nav overlap */}
                        {isMobile && <div className="h-20"></div>}
                      </>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
              </motion.div>
            ) : null}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CoverageSidebar;